import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import CheckoutButton from '../components-library/buttons/CheckoutButton';
import CompanyInfo from '../components-library/company/CompanyInfo';
import Footer from '../components-library/footers/Footer';
import LoadingMenuPage from '../components-library/loading/LoadingMenuPage';
import OngoingOrder from '../components-library/order/OngoingOrder';
import { SCREENS } from '../services/exports/Constants';
import { Order } from '../services/exports/Interfaces';
import OrderBasketScreen from './OrderBasketScreen';
import useScreenType from '../hooks/utility/useScreenType';
import CookieConsent from '../components-library/CookieConsent';
import CompanyDetailsModal from '../modals/CompanyDetailsModal';
import { useTranslation } from 'react-i18next';
import useInitialData from '../hooks/global/useInitialData';
import useAppState from '../hooks/global/useAppState';
import useRouter from '../hooks/navigation/useRouter';
import MenuCategoryList from '../components-library/menu/MenuCategoryList';
import OrderCarousel from '../components-library/carousel/OrderCarousel';
import OrderManager from '../services/api/OrderManager';
import useAuth from '../hooks/global/useAuth';
import collect from 'collect.js';
import PageLayout from '../components-library/layouts/PageLayout';
import OrderType from '../components-library/order-type/OrderType';
import useAppMethods from '../hooks/utility/useAppMethods';
import PoweredByFooter from '../components-library/footers/PoweredByFooter';
import DealsCarousel from '../components-library/carousel/DealsCarousel';
import useOrderFlow from '../hooks/order/useOrderFlow';
import SavingsBanner from '../components-library/banners/SavingsBanner';
import classnames from 'classnames';

export default function RestaurantDetailsScreen() {
  const { t } = useTranslation(null, { keyPrefix: 'Pages:RestaurantDetailsScreen' });

  const { data } = useInitialData();
  const { company } = data;

  const { isLoggedIn } = useAuth();
  const { isDesktop } = useScreenType();
  const { toggleOrderMethodModal } = useAppMethods();
  const { orderMethodModalProps, menuItemModalProps, menu } = useAppState();
  const { navigate } = useRouter();
  const { saveMenu } = useAppMethods();
  const { isOrderMethodInfoComplete, ongoingOrder } = useOrderFlow();

  const menuLoaded = menu?.menuLoaded;

  const [showCompanyDetailsModal, setShowCompanyDetailsModal] = useState(false);
  const [lastOrder, setLastOrder] = useState<Order | null>(null);

  const menuListRef = useRef(null);

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    menuLoaded && !isOrderMethodInfoComplete && setTimeout(() => toggleOrderMethodModal({ openModal: true }), 3000);
  }, [menuLoaded]);

  async function init(): Promise<void> {
    if (!isLoggedIn) {
      return;
    }

    const { response, success } = await OrderManager.getOrderHistory(company.id, { limit: 1 });

    if (success) {
      setLastOrder(collect(response.data.data).first());
    }
  }

  function toggleCompanyDetailsModal(): void {
    setShowCompanyDetailsModal(!showCompanyDetailsModal);
  }

  const renderBanner = useMemo(() => Boolean(ongoingOrder?.id) && <OngoingOrder />, [ongoingOrder?.id]);

  const renderLastOrder = (): ReactElement => {
    return (
      lastOrder && (
        <div className="w-full lg:mt-lg my-md">
          <OrderCarousel order={lastOrder} />
        </div>
      )
    );
  };

  const renderCheckoutButton = useMemo<ReactElement | undefined>(() => {
    return (
      !orderMethodModalProps?.openModal &&
      !menuItemModalProps?.openModal &&
      !isDesktop && (
        <div className="lg:hidden block sticky bottom-0 w-full z-50">
          <CheckoutButton
            title={t('checkout_button.title')}
            onClick={() =>
              navigate({
                web: {
                  to: SCREENS.BASKET,
                },
              })
            }
            hideAddProductsButton
          />
        </div>
      )
    );
  }, [orderMethodModalProps, isDesktop, menuItemModalProps]);

  const renderCompanyDetailsModal = useMemo<JSX.Element>(() => {
    return (
      <CompanyDetailsModal data={company} openModal={showCompanyDetailsModal} toggleModal={toggleCompanyDetailsModal} />
    );
  }, [company, showCompanyDetailsModal]);

  const renderLoadingMenu = useMemo(
    () =>
      !menuLoaded && (
        <LoadingMenuPage
          startAnimation={true}
          onAnimationComplete={() => {
            saveMenu({ menuLoaded: true });
          }}
        />
      ),
    [menuLoaded],
  );

  return (
    <PageLayout>
      <div>
        <SavingsBanner />
        <main className="h-screen w-full" data-test="restaurant-details-body">
          <div>{renderBanner}</div>
          <div className="space-y-[46px]">
            <CompanyInfo />
            <div className="px-md lg:hidden block w-full">
              <OrderType type={2} />
            </div>
          </div>
          <div>
            <div
              className={classnames('flex flex-row lg:pt-md mt-lg gap-lg', {
                'layout-container': isDesktop,
              })}
            >
              <div className="relative flex-1 min-w-0">
                <div className="pr-none lg:pr-md">
                  <MenuCategoryList
                    ref={menuListRef}
                    ListFooterComponent={<Footer />}
                    DiscountsComponent={<DealsCarousel />}
                    LastOrderComponent={renderLastOrder()}
                  />
                </div>
              </div>
              <div className="shrink-0 w-[400px] mt-xs overflow-hidden border-xs h-[96vh] lg:flex lg:flex-col hidden z-20 bg-gray-600 rounded-xl sticky top-md">
                <OrderBasketScreen customParentContainer="!h-[96vh]" />
              </div>
            </div>
          </div>
          {renderCheckoutButton}
          <PoweredByFooter />
        </main>
        {renderCompanyDetailsModal}
        {renderLoadingMenu}
        <CookieConsent />
      </div>
    </PageLayout>
  );
}
